import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

/**
 * Fetch all QR codes
 */
export function useQRCodes() {
  return useQuery({
    queryKey: ['qr-codes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('qr_codes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Fetch single QR code with scan statistics
 */
export function useQRCode(id) {
  return useQuery({
    queryKey: ['qr-code', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('qr_codes')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Fetch QR code scan statistics
 */
export function useQRCodeStats(qrCodeId) {
  return useQuery({
    queryKey: ['qr-code-stats', qrCodeId],
    queryFn: async () => {
      // Get scan history
      const { data: scans, error: scansError } = await supabase
        .from('qr_code_scans')
        .select('*')
        .eq('qr_code_id', qrCodeId)
        .order('scanned_at', { ascending: false })
        .limit(100);

      if (scansError) throw scansError;

      // Get orders from this QR code
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select('*, product_snapshot')
        .eq('qr_code_id', qrCodeId)
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;

      // Calculate stats
      const totalScans = scans?.length || 0;
      const totalOrders = orders?.length || 0;
      const totalRevenue = orders?.reduce((sum, order) => sum + Number(order.total_price || 0), 0) || 0;
      const conversionRate = totalScans > 0 ? (totalOrders / totalScans) * 100 : 0;

      // Group scans by date (last 7 days)
      const last7Days = Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - i);
        return date.toISOString().split('T')[0];
      }).reverse();

      const scansByDate = last7Days.map((date) => ({
        date,
        count: scans?.filter((s) => s.scanned_at.startsWith(date)).length || 0,
      }));

      const ordersByDate = last7Days.map((date) => ({
        date,
        count: orders?.filter((o) => o.created_at.startsWith(date)).length || 0,
        revenue: orders
          ?.filter((o) => o.created_at.startsWith(date))
          .reduce((sum, o) => sum + Number(o.total_price || 0), 0) || 0,
      }));

      return {
        totalScans,
        totalOrders,
        totalRevenue,
        conversionRate,
        scansByDate,
        ordersByDate,
        recentScans: scans?.slice(0, 10) || [],
        recentOrders: orders?.slice(0, 10) || [],
      };
    },
    enabled: !!qrCodeId,
    staleTime: 1000 * 30, // 30 seconds
  });
}

/**
 * Generate a new QR code
 */
export function useGenerateQRCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      // Get current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error('Not authenticated. Please log in again.');
      }

      const token = session.access_token;

      const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-qr-code`;
      
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      
      if (!res.ok) {
        console.error('QR Code generation error:', data);
        throw new Error(data.error || data.details || 'Failed to generate QR code');
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qr-codes'] });
    },
  });
}

/**
 * Update QR code
 */
export function useUpdateQRCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }) => {
      const { data, error } = await supabase
        .from('qr_codes')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qr-codes'] });
    },
  });
}

/**
 * Delete QR code
 */
export function useDeleteQRCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from('qr_codes')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qr-codes'] });
    },
  });
}
